import { Modal, useModals } from 'almanac-ui/Modal'
import Styles from './MediaFileViewerModal.module.scss'
import { MediaFileViewerModalProps } from './Types'
import { Heading } from 'blaze-ui/Heading/Heading'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Edit1 } from 'blaze-ui/icons'
import { useState } from 'react'
import { AlmanacFileTitleContainer } from 'components/AlmanacFileTitle/AlmanacFileTitleContainer'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { Text } from 'blaze-ui/Text/Text'
import { bytesToMb } from 'blaze-components/MediaUploadModal/utils'
import { isNumber } from 'lodash'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'

const MediaFileViewerModal = ({ file, actions, editEnabled = true, showTitle = true }: MediaFileViewerModalProps) => {
  const [title, setTitle] = useState(() => {
    if ('title' in file) {
      return file.title
    } else {
      // Extract file name from source_url and remove the random string at the end
      const url = file.source_url
      const match = url.match(/\/([^/]+)\.\w+$/)
      if (match) {
        const filenameWithExtension = match[1]
        const lastHyphenIndex = filenameWithExtension.lastIndexOf('-')
        if (lastHyphenIndex !== -1) {
          return `${filenameWithExtension.substring(0, lastHyphenIndex)}${url.substring(url.lastIndexOf('.'))}`
        }
        return filenameWithExtension
      }
    }
    return file.mime_type.split('/')[0]
  })

  const queryClient = useQueryClient()
  const { closeModal } = useModals()
  const workspaceId = useCurrentWorkspaceId()
  const { width, height, size } = file

  const resetFilesQueries = () => queryClient.invalidateQueries(['workspace', workspaceId, 'files'])

  return (
    <Modal.Root size="lg" onPressOutside={closeModal}>
      <Modal.Header>
        <div className={Styles.headerContainer}>
          <div className={Styles.titleContainer}>
            <div className={Styles.title}>
              <ResponsiveDesign.Desktop>
                <Modal.BackButton />
              </ResponsiveDesign.Desktop>
              {showTitle && <Heading level={3}>{title}</Heading>}

              {editEnabled && (
                <AlmanacFileTitleContainer
                  id={isNumber(file.id) ? file.id.toString() : file.id}
                  onChange={setTitle}
                  onUpdateFinish={resetFilesQueries}
                  assetType="MediaFile"
                  performAsyncUpdate
                  autoFocus
                  trigger={<IconButton icon={Edit1} variant="tertiary" />}
                />
              )}
            </div>
            {size && width && height && (
              <Text variant="secondary">
                {width} x {height}px, {bytesToMb(size).toFixed(1)}mb
              </Text>
            )}
          </div>
          <div className={Styles.actions}>{actions}</div>
        </div>
      </Modal.Header>
      <Modal.Content>
        <div className={Styles.filePreview}>
          {file.mime_type?.startsWith('image') && <img className={Styles.image} src={file.source_url} alt="" />}
          {file.mime_type?.startsWith('video') && (
            <video className={Styles.video} src={file.source_url} controls preload="auto" autoPlay />
          )}
          {file.mime_type?.startsWith('audio') && <audio className={Styles.audio} src={file.source_url} controls />}
        </div>
      </Modal.Content>
    </Modal.Root>
  )
}

export { MediaFileViewerModal }
